<template>
  <div
    v-if="modelValue"
    class="ijdb-overlay"
    data-testid="import-job-description-background-overlay"
  >
    <div class="ijdb-overlay__shell">
      <IQControlledOverlayHeader
        :title="headerTitle"
        :close-label="closeLabel"
        @close="handleClose"
      >
        <template v-if="showHeaderActions" #actions>
          <button
            v-if="isProcessing"
            type="button"
            class="ijdb-header-action ijdb-header-action--text"
            data-testid="ijdb-cancel-import"
            @click="showCancelDialog = true"
          >
            Cancel import
          </button>
          <button
            v-if="isProcessing"
            type="button"
            class="ijdb-header-action ijdb-header-action--fill"
            data-testid="ijdb-run-in-background"
            @click="handleClose"
          >
            Run in background
          </button>
          <button
            v-if="canDiscardRemaining"
            type="button"
            class="ijdb-header-action ijdb-header-action--text"
            data-testid="ijdb-discard-remaining"
            @click="showDiscardRemainingDialog = true"
          >
            Discard remaining
          </button>
        </template>
      </IQControlledOverlayHeader>

      <div class="ijdb-overlay__body">
        <section class="ijdb-chat" :class="{ 'ijdb-chat--split': showPreview }">
          <div class="ijdb-chat__pinned">
            <UploadedFileCardWithViewer
              v-if="fileName"
              :file-name="fileName"
              :file-type="fileType"
              :is-preview-open="showPreview"
              :disabled="!localFile"
              @open-viewer="previewOpen = true"
            />
          </div>

          <div ref="threadRef" class="ijdb-chat__thread" data-testid="ijdb-chat-thread">
            <p v-if="loading" class="ijdb-status" data-testid="ijdb-loading">Loading import ...</p>

            <div v-else-if="loadError" class="ijdb-ai" data-testid="ijdb-load-error">
              <span class="ijdb-ai__icon" aria-hidden="true"><Icon name="iq_small_icon" /></span>
              <div class="ijdb-ai__body">
                <p class="ijdb-ai__text" role="alert">{{ loadError }}</p>
                <div class="ijdb-ai__actions">
                  <StandardButton
                    type="magenta"
                    variant="primary"
                    size="small"
                    label="Try again"
                    data-testid="ijdb-reload"
                    @click="loadActive()"
                  />
                </div>
              </div>
            </div>

            <IQThinkingBanner
              v-else-if="isProcessing"
              data-testid="ijdb-thinking"
              state="thinking"
              title="IQ is looking for Job Description Information and is validating against context ..."
              caption="Analyzing your file ..."
            />

            <template v-else>
              <p v-if="noImport" class="ijdb-status" data-testid="ijdb-no-import">
                {{ NO_IMPORT_TEXT }}
              </p>

              <div
                v-for="message in thread"
                :key="message.id"
                class="ijdb-turn"
                :class="`ijdb-turn--${message.kind}`"
                :data-testid="`ijdb-turn-${message.kind}`"
              >
                <div v-if="message.kind === 'ai'" class="ijdb-ai">
                  <span class="ijdb-ai__icon" aria-hidden="true">
                    <Icon name="iq_small_icon" />
                  </span>
                  <div class="ijdb-ai__body">
                    <p class="ijdb-ai__text">{{ message.text }}</p>
                    <div v-if="message.item" class="ijdb-ai__dropdown" data-testid="ijdb-dropdown">
                      <JobDescriptionImportActionsDropDown
                        :bullet-text="message.item.text"
                        :matched-responsibility-id="message.item.matched_responsibility?.id"
                        :covering-roles="[]"
                        :available-actions="dropdownActions(message.item)"
                        @attach-direct="handleAttachDirect(message.item)"
                        @create-new="handleCreateNew(message.item)"
                        @discard="handleDiscard(message.item)"
                        @leave-as-is="handleLeaveAsIs(message.item)"
                      />
                    </div>
                    <div v-if="message.actions" class="ijdb-ai__actions">
                      <span
                        v-for="action in message.actions"
                        :key="action.type"
                        :data-testid="`ijdb-action-${action.type}`"
                      >
                        <StandardButton
                          type="magenta"
                          :variant="action.variant"
                          size="small"
                          :label="action.label"
                          @click="handleAction(action.type)"
                        />
                      </span>
                    </div>
                  </div>
                </div>
                <div v-else class="ijdb-user">
                  <span class="ijdb-user__bubble">{{ message.text }}</span>
                </div>
              </div>

              <div v-if="actionPending" class="ijdb-turn ijdb-turn--ai" data-testid="ijdb-pending">
                <div class="ijdb-ai ijdb-ai--pending">
                  <IQThinkingPulsatingIcon state="thinking" :size="24" />
                </div>
              </div>
            </template>
            <p v-if="actionError" class="ijdb-error" role="alert" data-testid="ijdb-error">
              {{ actionError }}
            </p>
          </div>
        </section>

        <aside v-if="showPreview" class="ijdb-preview" data-testid="ijdb-document-preview">
          <button
            type="button"
            class="ijdb-preview__close"
            aria-label="Close document preview"
            data-testid="ijdb-preview-close"
            @click="previewOpen = false"
          >
            <img :src="closeCircleSrc" alt="" width="18" height="18" />
          </button>
          <div class="ijdb-preview__body">
            <FilePreview :file="uploadedFile" />
          </div>
        </aside>
      </div>
    </div>

    <ImportConfirmDialog
      v-model="showCancelDialog"
      title="Cancel this import?"
      :body="cancelBody"
      keep-label="Keep processing"
      confirm-label="Cancel import"
      :loading="cancelling"
      @confirm="confirmCancel"
    />
    <ImportConfirmDialog
      v-model="showDiscardRemainingDialog"
      :title="discardRemainingTitle"
      :body="discardRemainingBody"
      keep-label="Keep reviewing"
      confirm-label="Discard remaining"
      :loading="discardingRemaining"
      @confirm="confirmDiscardRemaining"
    />
    <AddResponsibility
      :value="showCreateModal"
      :initialValue="createInitialName"
      :initialContext="createInitialContext"
      :isShowCharCounter="true"
      :max_char="120"
      :showContext="true"
      :loading="createSaving"
      :suggestedByIQ="createSuggestedByIQ"
      :errorMessage="createErrorMessage"
      :zIndex="11000"
      @input="handleCreateModalInput"
      @onSave="handleCreateModalSave"
      @clearError="createErrorMessage = ''"
    />
    <DiscardProposedChangesDialog
      :value="showSkipDialog"
      :z-index="11000"
      @input="showSkipDialog = !!$event"
      @onAction="handleSkipConfirm"
    />
  </div>
</template>

<script setup>
import { computed, nextTick, onUnmounted, ref, watch } from "vue";
import IQControlledOverlayHeader from "@components/IQControlledOverlayHeader/IQControlledOverlayHeader.vue";
import IQThinkingBanner from "@components/IQThinkingBanner/IQThinkingBanner.vue";
import IQThinkingPulsatingIcon from "@components/IQThinkingPulsatingIcon/IQThinkingPulsatingIcon.vue";
import FilePreview from "@components/FilePreview/FilePreview.vue";
import UploadedFileCardWithViewer from "@components/UploadedFileCardWithViewer/UploadedFileCardWithViewer.vue";
import JobDescriptionImportActionsDropDown from "@components/JobDescriptionImportActionsDropDown/JobDescriptionImportActionsDropDown.vue";
import ImportConfirmDialog from "@components/ImportConfirmDialog/ImportConfirmDialog.vue";
import StandardButton from "@components/StandardButton/StandardButton.vue";
import Icon from "@components/icons/Icon.vue";
import AddResponsibility from "@components/RolesCatalog/AddResponsibilityDialog/AddResponsibilityDialog.vue";
import DiscardProposedChangesDialog from "@components/DiscardProposedChangesDialog/DiscardProposedChangesDialog.vue";
import { buildFallbackSuggestion } from "@components/ImportJobDescriptionOverlay/importJobDescriptionDemoFlow.js";
import { getResponsibilityCreateErrorMessage } from "@components/utils/responsibilityNameError";
import {
  ACTION_ERROR_TEXT,
  COVERAGE_CHANGED_TEXT,
  DECISION_ACTION,
  DECISION_ERROR_TEXT,
  DONE_TEXT,
  EXPIRED_TEXT,
  FAILED_TEXT,
  IMPORT_OUTCOME_NOTHING_FOUND,
  IMPORT_STAGE,
  LOAD_ERROR_TEXT,
  NOTHING_FOUND_TEXT,
  NO_IMPORT_TEXT,
  REFRESHED_TEXT,
  buildIntroText,
  buildItemMessage,
  decisionLabel,
  importErrorCode,
  importErrorItem,
  isPendingItem,
  isProcessingStage,
  replaceItem,
} from "@components/utils/importJobDescriptionBackground";
import {
  cancelJobDescriptionImport,
  decideJobDescriptionImportItem,
  discardRemainingJobDescriptionImport,
  dismissJobDescriptionImport,
  getActiveJobDescriptionImport,
  retryJobDescriptionImport,
} from "@/api/positionAPI";

const POLL_INTERVAL_MS = 3000;
const OUTCOME = Object.freeze({ APPLIED: "applied", REFRESHED: "refreshed", FAILED: "failed" });

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  positionId: {
    type: [String, Number],
    default: null,
  },
  positionName: {
    type: String,
    default: "",
  },
  uploadedFile: {
    type: Object,
    default: () => null,
  },
});

const emit = defineEmits(["close", "request-new-file"]);

const closeCircleSrc = require("@components/assets/x-circle.svg");

const importRecord = ref(null);
const items = ref([]);
const loading = ref(false);
const loadError = ref("");
const actionError = ref("");
const actionPending = ref(false);
const previewOpen = ref(true);
const threadRef = ref(null);
const showCancelDialog = ref(false);
const cancelling = ref(false);
const showDiscardRemainingDialog = ref(false);
const discardingRemaining = ref(false);
const showSkipDialog = ref(false);
const showCreateModal = ref(false);
const createInitialName = ref("");
const createInitialContext = ref("");
const createSuggestedByIQ = ref(false);
const createSaving = ref(false);
const createErrorMessage = ref("");
let pendingItem = null;
let pollTimer = null;
let polling = false;
let disposed = false;
let loadGeneration = 0;

const stage = computed(() => importRecord.value?.stage || "");
const isProcessing = computed(() => isProcessingStage(stage.value));
const noImport = computed(() => !importRecord.value || stage.value === IMPORT_STAGE.CANCELLED);
const pendingItems = computed(() => items.value.filter(isPendingItem));
const nextItem = computed(() => pendingItems.value[0] || null);
const isReviewing = computed(() => stage.value === IMPORT_STAGE.AWAITING_DECISIONS);
const canDiscardRemaining = computed(() => isReviewing.value && pendingItems.value.length > 0);
const showHeaderActions = computed(() => isProcessing.value || canDiscardRemaining.value);
const closeLabel = computed(() => (isProcessing.value ? "Run in background" : "Close"));

const localFile = computed(() => {
  const uploaded = props.uploadedFile;
  if (!uploaded) return null;
  if (typeof File !== "undefined" && uploaded instanceof File) return uploaded;
  return uploaded.file || null;
});
const fileName = computed(
  () => props.uploadedFile?.name || importRecord.value?.original_filename || ""
);
const fileType = computed(() => props.uploadedFile?.type || "");
const showPreview = computed(() => previewOpen.value && !!localFile.value);

const headerTitle = computed(() => {
  const id = props.positionId != null && props.positionId !== "" ? `#${props.positionId}` : "";
  const target = [id, props.positionName || ""].filter(Boolean).join(" - ");
  return target
    ? `AI assisted Job Description Import for position ${target}`
    : "AI assisted Job Description Import";
});

const cancelBody = computed(
  () =>
    `IQ will stop processing ${
      fileName.value || "this file"
    } and the results will be discarded. You can also close this window and let IQ keep working in the background.`
);
const discardRemainingTitle = computed(() => {
  const count = pendingItems.value.length;
  return `Discard the ${count} remaining ${count === 1 ? "responsibility" : "responsibilities"}?`;
});
const discardRemainingBody = computed(() => {
  const decided = items.value.length - pendingItems.value.length;
  const count = pendingItems.value.length;
  return `The ${decided} ${
    decided === 1 ? "responsibility" : "responsibilities"
  } you already accepted stay on the position. IQ will drop the ${count} you have not decided on and the import will end.`;
});

const thread = computed(() => {
  const messages = [];
  const total = items.value.length;
  const showItems = isReviewing.value || stage.value === IMPORT_STAGE.COMPLETED;

  if (showItems && total > 0) {
    messages.push({ id: "intro", kind: "ai", text: buildIntroText(total, fileName.value) });
    for (const item of items.value) {
      if (isPendingItem(item)) {
        if (item.id === nextItem.value?.id) {
          messages.push({
            id: `item-${item.id}`,
            kind: "ai",
            text: buildItemMessage(item, total),
            item,
          });
        }
        break;
      }
      messages.push({ id: `item-${item.id}`, kind: "ai", text: buildItemMessage(item, total) });
      messages.push({ id: `decision-${item.id}`, kind: "user", text: decisionLabel(item) });
    }
  }

  if (stage.value === IMPORT_STAGE.COMPLETED) {
    if (importRecord.value?.outcome === IMPORT_OUTCOME_NOTHING_FOUND) {
      messages.push({
        id: "nothing-found",
        kind: "ai",
        text: NOTHING_FOUND_TEXT,
        actions: [{ type: "upload-new-file", label: "Upload a new file", variant: "primary" }],
      });
    } else {
      messages.push({ id: "done", kind: "ai", text: DONE_TEXT });
    }
  } else if (stage.value === IMPORT_STAGE.EXPIRED) {
    messages.push({
      id: "expired",
      kind: "ai",
      text: EXPIRED_TEXT,
      actions: [{ type: "upload-new-file", label: "Upload a new file", variant: "primary" }],
    });
  } else if (stage.value === IMPORT_STAGE.FAILED) {
    const reason = importRecord.value?.failure_reason;
    messages.push({
      id: "failed",
      kind: "ai",
      text: reason ? `${FAILED_TEXT} ${reason}` : FAILED_TEXT,
      actions: [
        { type: "retry", label: "Retry", variant: "primary" },
        { type: "close", label: "Close", variant: "secondary" },
      ],
    });
  }
  return messages;
});

watch(
  () => [props.modelValue, props.positionId, props.uploadedFile],
  ([open]) => {
    if (open) {
      loadActive();
      return;
    }
    stopPolling();
    resetState();
  },
  { immediate: true }
);

watch(
  () => [stage.value, props.modelValue],
  ([current, open]) => {
    if (open && isProcessingStage(current)) {
      schedulePoll();
    } else {
      stopPolling();
    }
  }
);

watch(
  () => thread.value.length,
  () => scrollThread()
);

onUnmounted(() => {
  disposed = true;
  stopPolling();
});

function resetState() {
  loadGeneration += 1;
  importRecord.value = null;
  items.value = [];
  loadError.value = "";
  actionError.value = "";
  loading.value = false;
}

function stopPolling() {
  if (pollTimer) {
    clearTimeout(pollTimer);
    pollTimer = null;
  }
}

function schedulePoll() {
  if (pollTimer || disposed) return;
  pollTimer = setTimeout(async () => {
    pollTimer = null;
    if (polling || disposed) return;
    polling = true;
    try {
      await loadActive({ silent: true });
    } finally {
      polling = false;
    }
    if (isProcessing.value && props.modelValue) schedulePoll();
  }, POLL_INTERVAL_MS);
}

function applySnapshot(data) {
  importRecord.value = data?.import || null;
  items.value = Array.isArray(data?.items) ? data.items : [];
}

async function loadActive({ silent = false } = {}) {
  if (props.positionId == null || props.positionId === "") return;
  const generation = ++loadGeneration;
  if (!silent) {
    loading.value = true;
    loadError.value = "";
  }
  try {
    const { data } = await getActiveJobDescriptionImport({ positionId: props.positionId });
    if (disposed || generation !== loadGeneration) return;
    applySnapshot(data);
    loadError.value = "";
  } catch {
    if (disposed || generation !== loadGeneration) return;
    if (!silent) loadError.value = LOAD_ERROR_TEXT;
  } finally {
    if (!silent && generation === loadGeneration) loading.value = false;
  }
}

async function scrollThread() {
  await nextTick();
  const el = threadRef.value;
  if (el) el.scrollTop = el.scrollHeight;
}

function dropdownActions(item) {
  return (item.available_actions || []).filter((a) => a !== DECISION_ACTION.ATTACH_VIA_ROLE);
}

async function dismissImport() {
  const importId = importRecord.value?.id;
  if (!importId) return;
  try {
    await dismissJobDescriptionImport({ positionId: props.positionId, importId });
  } catch {
    // a failed dismiss leaves the finished import on the server; closing still works.
  }
}

async function handleClose() {
  if (stage.value === IMPORT_STAGE.COMPLETED || stage.value === IMPORT_STAGE.EXPIRED) {
    await dismissImport();
  }
  emit("close");
}

async function confirmCancel() {
  const importId = importRecord.value?.id;
  if (!importId || cancelling.value) return;
  cancelling.value = true;
  actionError.value = "";
  try {
    await cancelJobDescriptionImport({ positionId: props.positionId, importId });
    showCancelDialog.value = false;
    emit("close");
  } catch {
    showCancelDialog.value = false;
    actionError.value = ACTION_ERROR_TEXT;
    await loadActive({ silent: true });
  } finally {
    cancelling.value = false;
  }
}

async function confirmDiscardRemaining() {
  const importId = importRecord.value?.id;
  if (!importId || discardingRemaining.value) return;
  discardingRemaining.value = true;
  actionError.value = "";
  try {
    await discardRemainingJobDescriptionImport({ positionId: props.positionId, importId });
    showDiscardRemainingDialog.value = false;
    await loadActive({ silent: true });
  } catch {
    showDiscardRemainingDialog.value = false;
    actionError.value = ACTION_ERROR_TEXT;
    await loadActive({ silent: true });
  } finally {
    discardingRemaining.value = false;
  }
}

async function handleAction(type) {
  if (type === "close") {
    emit("close");
    return;
  }
  if (type === "upload-new-file") {
    await dismissImport();
    emit("request-new-file");
    return;
  }
  if (type === "retry") {
    const importId = importRecord.value?.id;
    actionError.value = "";
    try {
      await retryJobDescriptionImport({ positionId: props.positionId, importId });
    } catch {
      actionError.value = ACTION_ERROR_TEXT;
    }
    await loadActive({ silent: true });
  }
}

async function handleDecisionError(error) {
  const code = importErrorCode(error);
  if (code === "ITEM_ALREADY_DECIDED" || code === "IMPORT_NOT_DECIDABLE") {
    actionError.value = REFRESHED_TEXT;
    await loadActive({ silent: true });
    return OUTCOME.REFRESHED;
  }
  if (code === "ITEM_COVERAGE_CHANGED") {
    const updated = importErrorItem(error);
    if (updated) {
      items.value = replaceItem(items.value, updated);
      actionError.value = COVERAGE_CHANGED_TEXT;
    } else {
      actionError.value = REFRESHED_TEXT;
      await loadActive({ silent: true });
    }
    return OUTCOME.REFRESHED;
  }
  actionError.value = DECISION_ERROR_TEXT;
  return OUTCOME.FAILED;
}

async function submitDecision(item, action, extra = {}) {
  const importId = importRecord.value?.id;
  if (!importId || actionPending.value) return { outcome: OUTCOME.FAILED, error: null };
  actionPending.value = true;
  actionError.value = "";
  try {
    const { data } = await decideJobDescriptionImportItem({
      positionId: props.positionId,
      importId,
      payload: { item_id: item.id, action, ...extra },
    });
    applySnapshot(data);
    return { outcome: OUTCOME.APPLIED, error: null };
  } catch (error) {
    return { outcome: await handleDecisionError(error), error };
  } finally {
    actionPending.value = false;
  }
}

function handleAttachDirect(item) {
  submitDecision(item, DECISION_ACTION.ATTACH_DIRECT, {
    responsibility_id: item.matched_responsibility?.id,
  });
}

function handleLeaveAsIs(item) {
  submitDecision(item, DECISION_ACTION.LEAVE_AS_IS);
}

function handleDiscard(item) {
  pendingItem = item;
  showSkipDialog.value = true;
}

function handleSkipConfirm() {
  showSkipDialog.value = false;
  if (pendingItem) submitDecision(pendingItem, DECISION_ACTION.SKIP);
  pendingItem = null;
}

function handleCreateNew(item) {
  const fallback = buildFallbackSuggestion(item.text);
  pendingItem = item;
  createErrorMessage.value = "";
  createInitialName.value = (item.suggested_name || fallback.name || "").trim();
  createInitialContext.value = fallback.context;
  createSuggestedByIQ.value = !!item.suggested_name;
  // Open on the next tick so the menu click is not treated as click-outside
  // on a freshly mounted v-dialog.
  nextTick(() => {
    showCreateModal.value = true;
  });
}

function handleCreateModalInput(open) {
  if (!open && createSaving.value) return;
  showCreateModal.value = !!open;
  if (!open) pendingItem = null;
}

async function handleCreateModalSave(payload) {
  const item = pendingItem;
  if (!item || createSaving.value) return;
  const name = payload?.name || createInitialName.value;
  const context = payload?.context ?? createInitialContext.value;
  createInitialName.value = name;
  createInitialContext.value = context;
  createErrorMessage.value = "";
  createSaving.value = true;
  const { outcome, error } = await submitDecision(item, DECISION_ACTION.CREATE_NEW, {
    suggested_name: name,
    suggested_context: context,
  });
  createSaving.value = false;
  if (outcome === OUTCOME.FAILED) {
    createErrorMessage.value = getResponsibilityCreateErrorMessage(error, DECISION_ERROR_TEXT);
    actionError.value = "";
    return;
  }
  showCreateModal.value = false;
  pendingItem = null;
}
</script>

<style scoped>
.ijdb-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  background: #ffffff;
}

.ijdb-overlay__shell {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  min-height: 0;
  background: #ffffff;
}

.ijdb-overlay__body {
  display: flex;
  flex: 1;
  min-height: 0;
}

.ijdb-header-action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  cursor: pointer;
}

.ijdb-header-action:focus-visible {
  outline: 2px solid #6941c6;
  outline-offset: 2px;
}

.ijdb-header-action--text {
  padding: 0;
  border: none;
  background: transparent;
  color: #344054;
}

.ijdb-header-action--fill {
  min-width: 155px;
  height: 36px;
  padding: 8px 14px;
  border: 1px solid #f2edff;
  border-radius: 8px;
  background: #f2edff;
  color: #6941c6;
}

.ijdb-chat {
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 0;
  min-height: 0;
  padding: 0 30px;
  border-right: 1px solid #eaecf0;
}

.ijdb-chat--split {
  width: 50%;
  flex: 0 0 50%;
}

.ijdb-chat__pinned {
  flex-shrink: 0;
  padding-top: 28px;
}

.ijdb-chat__thread {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 17px 0 28px;
}

.ijdb-status,
.ijdb-error {
  margin: 0;
  padding: 5px 10px;
  font-family: Inter, sans-serif;
  font-size: 14px;
  line-height: 22px;
}

.ijdb-status {
  color: #475467;
}

.ijdb-error {
  color: #b42318;
}

.ijdb-turn {
  width: 100%;
}

.ijdb-turn + .ijdb-turn {
  margin-top: 10px;
}

.ijdb-ai {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 5px 10px;
}

.ijdb-ai--pending {
  overflow: visible;
  padding-top: 8px;
  padding-bottom: 8px;
}

.ijdb-ai__icon {
  display: inline-flex;
  flex-shrink: 0;
  width: 28px;
  height: 28px;
}

.ijdb-ai__icon :deep(svg) {
  width: 28px;
  height: 28px;
}

.ijdb-ai__body {
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
  flex: 1;
}

.ijdb-ai__text {
  margin: 0;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
  color: #101828;
  white-space: pre-wrap;
}

.ijdb-ai__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.ijdb-user {
  display: flex;
  justify-content: flex-end;
  width: 100%;
}

.ijdb-user__bubble {
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  padding: 12px 16px;
  background: #f2f4f7;
  border-radius: 8px;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 22px;
  color: #344054;
}

.ijdb-preview {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 50%;
  flex: 0 0 50%;
  min-width: 0;
  min-height: 0;
  border-left: 1px solid #eaecf0;
  background: #ffffff;
}

.ijdb-preview__close {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 3px;
  border: none;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.92);
  cursor: pointer;
}

.ijdb-preview__close:hover {
  background: #f9fafb;
}

.ijdb-preview__body {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  background: #ffffff;
}
</style>
